import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Grid,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import MKTypography from "../components/MKTypography";
import MKBox from "../components/MKBox";
import MKButton from "../components/MKButton";
import { useTheme } from "../ThemeContext";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { selectUser, updateUser } from "../store/userSlice";
import API_BASE_URL from "../apiConfig";
import AvatarSelector from "../components/AvatarSelector";
import { PRIMARY_PINK } from "../constants";

function UserSettingsPage() {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { darkMode, toggleTheme } = useTheme();
  
  const [displayName, setDisplayName] = useState(user?.display_name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState(false);

  // Debug logging for created_at
  useEffect(() => {
    if (user?.created_at) {
      
    }
  }, [user?.created_at]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    if (!displayName.trim()) {
      setError("Display name is required");
      return;
    }

    if (displayName.trim().length < 2) {
      setError("Display name must be at least 2 characters long");
      return;
    }

    if (displayName.trim().length > 50) {
      setError("Display name must be less than 50 characters");
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          display_name: displayName.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update profile");
      }

      const updatedUser = await response.json();
      dispatch(updateUser(updatedUser));
      setSuccess("Profile updated successfully!");
    } catch (error) {
      console.error("Update profile error:", error);
      setError(error.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarSelect = async (avatarUrl) => {
    if (!user?.id) return;
    
    setUpdatingAvatar(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          avatar_url: avatarUrl
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update avatar");
      }

      const updatedUser = await response.json();
      dispatch(updateUser(updatedUser));
      setSuccess("Avatar updated successfully!");
    } catch (error) {
      console.error("Update avatar error:", error);
      setError(error.message || "Failed to update avatar");
    } finally {
      setUpdatingAvatar(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <MKTypography variant="h4" component="h1" gutterBottom sx={{ mb: 4, color: '#e91e63' }}>
        User Settings
      </MKTypography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Card>
        <CardContent>
          <MKTypography variant="h6" gutterBottom sx={{ color: '#e91e63' }}>
            Profile Information
          </MKTypography>
          
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Avatar Section */}
              <Grid item xs={12} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={user?.avatar_url}
                      sx={{ 
                        width: 120, 
                        height: 120
                      }}
                    >
                      {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <IconButton
                      onClick={() => setShowAvatarSelector(true)}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: '#e91e63',
                        color: 'white',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#c2185b'
                        }
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <MKTypography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    Profile Picture
                  </MKTypography>
                  <MKTypography variant="caption" color="text.secondary" align="center">
                    Click the edit button to choose an avatar
                  </MKTypography>
                </Box>
              </Grid>

              {/* Form Fields */}
              <Grid item xs={12} sm={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={user?.email || ''}
                      disabled
                      helperText="Email cannot be changed"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Enter your display name"
                      helperText="This will be shown to other users"
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Member Since"
                      value={(() => {
                        if (!user?.created_at) return 'N/A';
                        try {
                          const date = new Date(user.created_at);
                          if (isNaN(date.getTime())) return 'N/A';
                          return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        } catch (error) {
                          console.error('Date parsing error:', error);
                          return 'N/A';
                        }
                      })()}
                      disabled
                      helperText="When you joined the platform"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <MKButton
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  bgcolor: "#000000", 
                  color: "#FFFFFF", 
                  fontWeight: 600, 
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  "&:hover": {
                    bgcolor: "#333333"
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
              </MKButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <MKTypography variant="h6" gutterBottom sx={{ color: '#e91e63' }}>
            Appearance
          </MKTypography>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={toggleTheme}
                sx={{
                  '& .MuiSwitch-root': {
                    '& .MuiSwitch-switchBase': {
                      color: darkMode ? PRIMARY_PINK : '#ccc',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: PRIMARY_PINK,
                      '&:hover': {
                        backgroundColor: 'rgba(233, 30, 99, 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: PRIMARY_PINK,
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: darkMode ? PRIMARY_PINK : '#ccc',
                    },
                  },
                }}
              />
            }
            label="Dark Mode"
            labelPlacement="end"
          />
          <MKTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Toggle between light and dark theme. Your preference will be saved automatically.
          </MKTypography>
        </CardContent>
      </Card>

      {/* Avatar Selector Modal */}
      <AvatarSelector
        open={showAvatarSelector}
        currentAvatar={user?.avatar_url}
        onSelect={handleAvatarSelect}
        onClose={() => setShowAvatarSelector(false)}
      />
    </Box>
  );
}

export default UserSettingsPage; 